import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ArrowDown, ArrowRightLeft, ArrowUp, BellRing, ChevronDown, ChevronRight, ChevronUp, MailPlus, MailX, MoveRight, Pencil, Plus,
  Trash2, Undo2, UserPlus, X, XCircle,
} from 'lucide-react';
import {
  canRemindPerson, childrenOf, confirmedInSubtree, entryPath, formatDateTime, formatDay, historyOf,
  invitedInSubtree, isOpenSpot, mailStateOf, nodeTitle, pendingSuggestionOf, personReminders, placeOf, roomName, spotLabel,
  spotCapabilities, spotsOfPerson, subtreeSpots, suggestedPersonOf, venueText,
} from './programAdminModel';
import { OpenSpotTag, RoleText, StateTag } from './StateTag';
import PersonPanel from './PersonPanel';
import SlidesPanel from './SlidesPanel';
import {
  HistoryList, HistoryRow, HistoryToggle, HistoryWhen, LinkButton, MiniButton, MiniButtonPrimary, Panel, PanelActions,
  PanelClose, PanelEyebrow, PanelHead, PanelHint, PanelLabel, PanelRow, PanelSection, PanelSectionTitle,
  MailBox, PanelTitle, PersonMeta, RoleCell, SuggestionBox, SuggestionIntro,
} from './programAdminStyles';

// The statuses whose name the public program shows only when the spot allows it (PublicProgramService.NAMED_EARLY).
const NAMED_EARLY = ['PROPOSED', 'INVITED'];

// An action that only applies in some states is disabled rather than hidden, with the reason in its
// tooltip: the admin sees what the state forbids and why (plan §7.7 item 7).
const ActionButton = ({ disabled, reason, onClick, danger, primary, children }) => {
  const Button = primary ? MiniButtonPrimary : MiniButton;
  return (
    <Button
      type="button"
      disabled={disabled}
      $danger={danger}
      title={disabled && reason ? reason : undefined}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </Button>
  );
};

const disabledHint = (intl, key, def) => intl.formatMessage({ id: key, defaultMessage: def });

// Whether an invitation of this spot is still waiting for an answer, and who was asked.
const historyLine = (intl, row, people) => {
  const person = (people || []).find((p) => p.personId === row.personId);
  const asked = formatDateTime(intl, row.askedAt);
  const closed = row.closedAt ? formatDateTime(intl, row.closedAt) : null;
  return (
    <>
      <div>
        <FormattedMessage id="programAdmin.history.asked" defaultMessage="Asked {when}" values={{ when: <HistoryWhen>{asked}</HistoryWhen> }} />
        {person ? <> — {person.programName}</> : null}
      </div>
      {closed && (
        <div>
          {row.outcome === 'DECLINED' && (
            <FormattedMessage id="programAdmin.history.declined"
              defaultMessage="Declined {when}{by, select, ADMIN { (recorded by the organizers)} other {}}"
              values={{ when: <HistoryWhen>{closed}</HistoryWhen>, by: row.decidedBy || 'PERSON' }} />
          )}
          {row.outcome === 'CONFIRMED' && (
            <FormattedMessage id="programAdmin.history.confirmed" defaultMessage="Confirmed {when}" values={{ when: <HistoryWhen>{closed}</HistoryWhen> }} />
          )}
          {row.outcome === 'CANCELLED' && (
            <FormattedMessage id="programAdmin.history.cancelled" defaultMessage="Uninvited {when}" values={{ when: <HistoryWhen>{closed}</HistoryWhen> }} />
          )}
          {row.outcome === 'SUPERSEDED' && (
            <FormattedMessage id="programAdmin.history.superseded" defaultMessage="Replaced {when}" values={{ when: <HistoryWhen>{closed}</HistoryWhen> }} />
          )}
        </div>
      )}
      {row.withdrawnAt && (
        <div>
          <FormattedMessage id="programAdmin.history.withdrawn" defaultMessage="Withdrew after confirming {when}" values={{ when: <HistoryWhen>{formatDateTime(intl, row.withdrawnAt)}</HistoryWhen> }} />
        </div>
      )}
      {(row.declineReplacementName || row.declineReplacementEmail) && (
        <div>
          <strong><FormattedMessage id="programAdmin.history.suggested" defaultMessage="Suggested:" /></strong>{' '}
          {[row.declineReplacementName, row.declineReplacementEmail].filter(Boolean).join(' — ')}
        </div>
      )}
      {!closed && <div><FormattedMessage id="programAdmin.history.waiting" defaultMessage="No answer yet." /></div>}
    </>
  );
};

// A spot: who holds it (or the label printed meanwhile), its state, what the decline suggested, the
// invitation trail, and exactly the actions the backend allows in this state (plan §7.7 items 5, 7
// and 8; the rules are in programAdminModel.spotCapabilities).
const SpotPanel = ({ program, language, spot, entry, people, actions = {}, onOpenPerson, onClose }) => {
  const intl = useIntl();
  const [historyOpen, setHistoryOpen] = useState(false);
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const { room, venue } = placeOf(program, entry.roomId);
  const history = historyOf(spot);
  const open = isOpenSpot(spot);
  const suggestion = pendingSuggestionOf(spot);
  const suggestedPerson = suggestedPersonOf(spot, people);
  const person = spot.personId != null ? (people || []).find((p) => p.personId === spot.personId) : null;
  const reminders = personReminders(person);
  const mail = spot.status === 'INVITED' && !spot.removedAt ? mailStateOf(spot, person) : null;
  const caps = spotCapabilities(spot, !!entry.removedAt);

  return (
    <Panel aria-label={t('programAdmin.panel.spot', 'Selected spot')}>
      <PanelHead>
        <div>
          {/* The spot panel is about the place on the program (role, state, invitation); the person
              panel, opened from the name, is about who they are. */}
          <PanelEyebrow>
            <FormattedMessage id="programAdmin.panel.spotEyebrow" defaultMessage="Spot" /> · <RoleText role={spot.role} />
          </PanelEyebrow>
          <PanelTitle>{spotLabel(spot) || t('programAdmin.tba', 'TBA')}</PanelTitle>
          <div style={{ marginTop: '.35rem' }}><StateTag status={spot.status} /></div>
        </div>
        <PanelClose type="button" onClick={onClose} aria-label={t('programAdmin.panel.close', 'Close the panel')}>
          <X size={18} />
        </PanelClose>
      </PanelHead>

      {/* The state itself is in the header; what follows from it is said here. */}
      {(spot.removedAt || open) && (
        <PanelSection>
          <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
            {open && !spot.removedAt && <OpenSpotTag />}
            {spot.removedAt && <PersonMeta as="span"><FormattedMessage id="programAdmin.removed" defaultMessage="Removed" /></PersonMeta>}
          </div>
          {spot.removedAt && (
            <PanelHint><FormattedMessage id="programAdmin.panel.removedHint" defaultMessage="This spot was removed; its history is kept." /></PanelHint>
          )}
          {!spot.removedAt && open && (
            <PanelHint><FormattedMessage id="programAdmin.panel.openHint" defaultMessage="The public program prints the label while no name is shown." /></PanelHint>
          )}
        </PanelSection>
      )}

      {suggestion && (
        <PanelSection>
          <SuggestionBox data-testid="suggestion-callout">
            <SuggestionIntro>
              <strong><FormattedMessage id="programAdmin.suggestion.headline" defaultMessage="Replacement suggested" /></strong>
              <div>
                <FormattedMessage id="programAdmin.suggestion.intro"
                  defaultMessage="{name} declined and suggested a replacement:"
                  values={{ name: spot.personName }} />
              </div>
              <PersonMeta as="div">
                {[suggestion.name, suggestion.email].filter(Boolean).join(' — ')}
              </PersonMeta>
              {suggestion.askedAt && (
                <PersonMeta as="div">
                  <FormattedMessage id="programAdmin.suggestion.declinedOn" defaultMessage="Declined {when}" values={{ when: formatDateTime(intl, suggestion.askedAt) }} />
                </PersonMeta>
              )}
            </SuggestionIntro>
            <MiniButtonPrimary
              type="button"
              onClick={() => actions.useSuggestion?.(spot, entry, { name: suggestion.name, email: suggestion.email, personId: suggestedPerson?.personId ?? null })}
            >
              <UserPlus size={13} aria-hidden="true" />
              <FormattedMessage id="programAdmin.suggestion.put" defaultMessage="Put {name} on this spot"
                values={{ name: suggestedPerson?.programName || suggestion.name || suggestion.email }} />
            </MiniButtonPrimary>
            {!suggestedPerson && (
              <PanelHint><FormattedMessage id="programAdmin.suggestion.unknown" defaultMessage="Not among this event's people. The next step shows who already exists with this address, the same name or a similar one; otherwise the person is created." /></PanelHint>
            )}
            {!suggestedPerson && !suggestion.email && (
              <PanelHint><FormattedMessage id="programAdmin.suggestion.noEmail" defaultMessage="No email was given: add the address on the person before inviting them." /></PanelHint>
            )}
          </SuggestionBox>
        </PanelSection>
      )}


      <PanelSection>
        <PanelSectionTitle><FormattedMessage id="programAdmin.panel.entry" defaultMessage="On this entry" /></PanelSectionTitle>
        <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.entryTitle" defaultMessage="Entry" /></PanelLabel><span>{nodeTitle(entry, language)}</span></PanelRow>
        <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.when" defaultMessage="Day" /></PanelLabel><span>{entryPath(program, entry, language)}</span></PanelRow>
        {entry.depth === 0 && room && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.room" defaultMessage="Room" /></PanelLabel>
            <span>{roomName(language, room)}{venue ? ` · ${venueText(venue)}` : ''}</span>
          </PanelRow>
        )}
      </PanelSection>

      <PanelSection>
        <PanelSectionTitle><FormattedMessage id="programAdmin.panel.person" defaultMessage="The person" /></PanelSectionTitle>
        {person ? (
          <>
            <PanelRow>
              <PanelLabel><FormattedMessage id="programAdmin.panel.name" defaultMessage="Name" /></PanelLabel>
              <LinkButton type="button" onClick={() => onOpenPerson?.(person.personId)}>{person.programName}</LinkButton>
            </PanelRow>
            {person.institution && <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.institution" defaultMessage="Institution" /></PanelLabel><span>{person.institution}</span></PanelRow>}
            <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.email" defaultMessage="Email" /></PanelLabel><span>{person.contactEmail}</span></PanelRow>
            {mail && (
              <MailBox data-testid="spot-mail-state">
                <PanelRow>
                  <PanelLabel><FormattedMessage id="programAdmin.panel.invitedOn" defaultMessage="Invited" /></PanelLabel>
                  <span>{formatDateTime(intl, mail.askedAt)}</span>
                </PanelRow>
                <PanelRow>
                  <PanelLabel><FormattedMessage id="programAdmin.panel.lastMail" defaultMessage="Last email" /></PanelLabel>
                  <span style={{ fontWeight: 700 }}>
                    {formatDateTime(intl, mail.lastSentAt)}
                    {' · '}
                    {mail.reminder
                      ? <FormattedMessage id="programAdmin.panel.lastMailReminder" defaultMessage="reminder {sent} of {max}" values={{ sent: mail.remindersSent, max: mail.maxReminders }} />
                      : <FormattedMessage id="programAdmin.panel.lastMailInvite" defaultMessage="the invitation" />}
                  </span>
                </PanelRow>
                <PanelRow>
                  <PanelLabel><FormattedMessage id="programAdmin.panel.nextReminder" defaultMessage="Next reminder" /></PanelLabel>
                  <span>
                    {mail.stopped
                      ? <FormattedMessage id="programAdmin.panel.nextReminderStopped" defaultMessage="none - reminders are stopped" />
                      : mail.nextReminder
                        ? <FormattedMessage id="programAdmin.panel.nextReminderOn" defaultMessage="automatically on {date}" values={{ date: formatDay(intl, mail.nextReminder) }} />
                        : <FormattedMessage id="programAdmin.panel.nextReminderNone" defaultMessage="none - all {max} reminders were sent" values={{ max: mail.maxReminders }} />}
                  </span>
                </PanelRow>
              </MailBox>
            )}
            <PanelActions style={{ marginTop: '.5rem' }}>
              <MiniButton type="button" onClick={() => onOpenPerson?.(person.personId)}>
                <FormattedMessage id="programAdmin.panel.openPerson" defaultMessage="Open person" /><ChevronRight size={13} aria-hidden="true" />
              </MiniButton>
            </PanelActions>
          </>
        ) : (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.label" defaultMessage="Printed as" /></PanelLabel>
            <span>{spot.placeholderLabel || t('programAdmin.tba', 'TBA')}</span>
          </PanelRow>
        )}
        {spot.personId != null && spot.placeholderLabel && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.placeholder" defaultMessage="Label meanwhile" /></PanelLabel>
            <span>{spot.placeholderLabel}</span>
          </PanelRow>
        )}
        {spot.groupLabel && (
          <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.group" defaultMessage="Group" /></PanelLabel><span>{spot.groupLabel}</span></PanelRow>
        )}
        {spot.displayNote && (
          <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.note" defaultMessage="Display note" /></PanelLabel><span>{spot.displayNote}</span></PanelRow>
        )}
        {/* PublicProgramService: a confirmed person is always named; a proposed or invited one only
            when the spot says so; nobody else. */}
        {spot.personId != null && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.showPublicly" defaultMessage="Name before confirmation" /></PanelLabel>
            {NAMED_EARLY.includes(spot.status) ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                {spot.showPublicly
                  ? <FormattedMessage id="programAdmin.panel.showPubliclyOn" defaultMessage="Shown publicly" />
                  : <FormattedMessage id="programAdmin.panel.showPubliclyOff" defaultMessage="Hidden until they confirm" />}
                {!entry.removedAt && (
                  <MiniButton type="button" aria-pressed={!!spot.showPublicly} onClick={() => actions.toggleShowPublicly?.(spot, entry)}>
                    {spot.showPublicly
                      ? <FormattedMessage id="programAdmin.panel.showPubliclyHide" defaultMessage="Hide" />
                      : <FormattedMessage id="programAdmin.panel.showPubliclyShow" defaultMessage="Show now" />}
                  </MiniButton>
                )}
              </span>
            ) : (
              <span>
                {spot.status === 'CONFIRMED'
                  ? <FormattedMessage id="programAdmin.panel.showPubliclyConfirmed" defaultMessage="Shown (confirmed)" />
                  : <FormattedMessage id="programAdmin.panel.showPubliclyNever" defaultMessage="Not shown" />}
              </span>
            )}
          </PanelRow>
        )}
        {person && reminders.openInvitations > 0 && (
          <PanelHint>
            <FormattedMessage id="programAdmin.panel.reminderState"
              defaultMessage="{open, plural, one {# open invitation} other {# open invitations}} of this person at this event · {sent, plural, =0 {no reminder sent yet} other {# reminders sent}} of {max}{stopped, select, yes { · reminders stopped} other {}}"
              values={{ open: reminders.openInvitations, sent: reminders.remindersSent || 0, max: reminders.maxReminders, stopped: reminders.remindersStopped ? 'yes' : 'no' }} />
          </PanelHint>
        )}
      </PanelSection>

      <PanelSection>
        <PanelSectionTitle><FormattedMessage id="programAdmin.panel.actions" defaultMessage="Actions" /></PanelSectionTitle>
        {caps.removed ? (
          <PanelHint>
            <FormattedMessage id="programAdmin.panel.noActions"
              defaultMessage="A removed spot cannot be changed. Add a new spot if this one is needed again; the history above stays readable." />
          </PanelHint>
        ) : (
          <>
            <PanelActions>
              <ActionButton primary disabled={!caps.invite} onClick={() => actions.inviteSpot?.(spot, entry)}
                reason={spot.personId == null
                  ? disabledHint(intl, 'programAdmin.spot.needsPerson', 'A spot without a person cannot be invited - put somebody on it first.')
                  : spot.status === 'INVITED'
                    ? disabledHint(intl, 'programAdmin.spot.alreadyInvited', 'An invitation is open already - send a reminder instead.')
                    : disabledHint(intl, 'programAdmin.spot.confirmedFirst', 'A confirmed person is withdrawn first.')}>
                <MailPlus size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.invite" defaultMessage="Invite" />
              </ActionButton>
              <ActionButton disabled={!caps.remind || !canRemindPerson(person)} onClick={() => actions.remindSpot?.(spot, entry)}
                reason={spot.status !== 'INVITED'
                  ? disabledHint(intl, 'programAdmin.spot.remindOnlyInvited', 'Only an open invitation can be reminded.')
                  : reminders.remindersStopped
                    ? disabledHint(intl, 'programAdmin.spot.remindersStopped', 'Their reminders are stopped - resume them first (People area).')
                    : disabledHint(intl, 'programAdmin.spot.nothingOpenToRemind', 'This person has no open invitation to remind.')}>
                <BellRing size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.remind" defaultMessage="Remind" />
              </ActionButton>
              <ActionButton disabled={!caps.uninvite} onClick={() => actions.uninviteSpot?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.uninviteOnlyInvited', 'Only an open invitation can be uninvited.')}>
                <MailX size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.uninvite" defaultMessage="Uninvite" />
              </ActionButton>
            </PanelActions>
            <PanelActions style={{ marginTop: '.4rem' }}>
              <ActionButton disabled={!caps.replace} onClick={() => actions.replacePerson?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.confirmedFirst', 'A confirmed person is withdrawn first.')}>
                <ArrowRightLeft size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.replace" defaultMessage="Replace person" />
              </ActionButton>
              <ActionButton disabled={!caps.decline} onClick={() => actions.markDeclined?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.declineOnlyInvited', 'Only an open invitation can be recorded as declined.')}>
                <XCircle size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.decline" defaultMessage="Mark declined" />
              </ActionButton>
              <ActionButton disabled={!caps.withdraw} onClick={() => actions.withdrawSpot?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.withdrawOnlyConfirmed', 'Only a confirmed person can be withdrawn.')}>
                <Undo2 size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.withdraw" defaultMessage="Withdraw" />
              </ActionButton>
            </PanelActions>
            <PanelActions style={{ marginTop: '.4rem' }}>
              <ActionButton disabled={!caps.move} onClick={() => actions.moveSpot?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.moveOnlyProposed', 'Only a spot nobody was asked about yet can move to another entry.')}>
                <MoveRight size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.move" defaultMessage="Move to another entry…" />
              </ActionButton>
              <ActionButton danger disabled={!caps.remove} onClick={() => actions.removeSpot?.(spot, entry)}
                reason={disabledHint(intl, 'programAdmin.spot.confirmedFirst', 'A confirmed person is withdrawn first.')}>
                <Trash2 size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.spot.remove" defaultMessage="Remove" />
              </ActionButton>
            </PanelActions>
            <PanelHint>
              <FormattedMessage id="programAdmin.panel.notifyHint"
                defaultMessage="An invitation, an uninvite, a replacement, a withdrawal and a removal show the email to the person first; a reminder always goes out per person." />
            </PanelHint>
          </>
        )}
      </PanelSection>

      <PanelSection>
        <HistoryToggle type="button" onClick={() => setHistoryOpen((v) => !v)} aria-expanded={historyOpen}>
          {historyOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <FormattedMessage id="programAdmin.panel.history" defaultMessage="History of this spot ({count})" values={{ count: history.length }} />
        </HistoryToggle>
        {historyOpen && (
          history.length > 0 ? (
            <HistoryList>
              {history.map((row) => <HistoryRow key={row.invitationId}>{historyLine(intl, row, people)}</HistoryRow>)}
            </HistoryList>
          ) : (
            <PanelHint><FormattedMessage id="programAdmin.history.empty" defaultMessage="Nobody has been asked about this spot yet." /></PanelHint>
          )
        )}
      </PanelSection>
    </Panel>
  );
};

// An entry: what it is, where it sits, what hangs off it, and the structure actions. "Up"/"down" are
// off at the ends of the siblings (the backend refuses to swap the first with nothing), and the
// removal is off while any spot of this entry or below it is confirmed (plan §7.6 "Removal").
const EntryPanel = ({ program, language, entry, position, actions = {}, onChanged, onClose }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const { room, venue } = placeOf(program, entry.roomId);
  const spots = subtreeSpots(entry);
  const openSpots = spots.filter(({ assignment }) => isOpenSpot(assignment)).length;
  const confirmed = confirmedInSubtree(entry);
  const invited = invitedInSubtree(entry);
  const removed = !!entry.removedAt;
  const canRemove = !removed && !confirmed;
  // ProgramService.settleByTime: an entry with a start time stands where its time puts it.
  const timed = !!entry.startTime;

  return (
    <Panel aria-label={t('programAdmin.panel.entry', 'On this entry')}>
      <PanelHead>
        <div>
          <PanelEyebrow>
            {entry.kind} · <FormattedMessage id="programAdmin.panel.entryEyebrow" defaultMessage="Entry" />
          </PanelEyebrow>
          <PanelTitle>{nodeTitle(entry, language)}</PanelTitle>
        </div>
        <PanelClose type="button" onClick={onClose} aria-label={t('programAdmin.panel.close', 'Close the panel')}>
          <X size={18} />
        </PanelClose>
      </PanelHead>

      <PanelSection>
        <PanelRow><PanelLabel><FormattedMessage id="programAdmin.panel.when" defaultMessage="Day" /></PanelLabel><span>{entryPath(program, entry, language)}</span></PanelRow>
        {entry.depth === 0 && room && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.room" defaultMessage="Room" /></PanelLabel>
            <span>{roomName(language, room)}{venue ? ` · ${venueText(venue)}` : ''}</span>
          </PanelRow>
        )}
        <PanelRow>
          <PanelLabel><FormattedMessage id="programAdmin.panel.visibility" defaultMessage="Visibility" /></PanelLabel>
          <span>
            {entry.visibility === 'INTERNAL'
              ? <FormattedMessage id="programAdmin.visibility.INTERNAL" defaultMessage="Internal only" />
              : <FormattedMessage id="programAdmin.visibility.PUBLIC" defaultMessage="Public" />}
          </span>
        </PanelRow>
        <PanelRow>
          <PanelLabel><FormattedMessage id="programAdmin.panel.asksForSlides" defaultMessage="Asks for slides" /></PanelLabel>
          <span>
            {entry.asksForSlides
              ? <FormattedMessage id="programAdmin.panel.asksForSlidesYes" defaultMessage="Yes" />
              : <FormattedMessage id="programAdmin.panel.asksForSlidesNo" defaultMessage="No" />}
          </span>
        </PanelRow>
        {entry.discussionMinutes != null && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.discussion" defaultMessage="Discussion" /></PanelLabel>
            <span><FormattedMessage id="programAdmin.panel.minutes" defaultMessage="{minutes} min" values={{ minutes: entry.discussionMinutes }} /></span>
          </PanelRow>
        )}
        <PanelRow>
          <PanelLabel><FormattedMessage id="programAdmin.panel.spots" defaultMessage="Spots" /></PanelLabel>
          <RoleCell>
            <FormattedMessage id="programAdmin.panel.spotCount"
              defaultMessage="{count, plural, =0 {none} one {# spot} other {# spots}}{open, plural, =0 {} other { · # needs filling}}{below, plural, =0 {} other { (including # below this entry)}}"
              values={{ count: spots.length, open: openSpots, below: spots.length - (entry.assignments || []).length }} />
          </RoleCell>
        </PanelRow>
        {childrenOf(entry).length > 0 && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.children" defaultMessage="Entries below" /></PanelLabel>
            <span>{childrenOf(entry).length}</span>
          </PanelRow>
        )}
        {entry.internalNotes && (
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.panel.internalNotes" defaultMessage="Internal note" /></PanelLabel>
            <span>{entry.internalNotes}</span>
          </PanelRow>
        )}
      </PanelSection>

      <PanelSection>
        <PanelSectionTitle><FormattedMessage id="programAdmin.panel.actions" defaultMessage="Actions" /></PanelSectionTitle>
        {removed ? (
          <PanelHint>
            <FormattedMessage id="programAdmin.panel.entryNoActions"
              defaultMessage="This entry was removed from the program; nothing can be changed on it. Its history stays readable in the spot panels." />
          </PanelHint>
        ) : (
          <>
            <PanelActions>
              <MiniButton type="button" onClick={() => actions.editEntry?.(entry)}>
                <Pencil size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.edit" defaultMessage="Edit" />
              </MiniButton>
              <MiniButton type="button" onClick={() => actions.addChild?.(entry)}>
                <Plus size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.addChild" defaultMessage="Add child" />
              </MiniButton>
              <MiniButton type="button" onClick={() => actions.addSpot?.(entry)}>
                <UserPlus size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.addSpot" defaultMessage="Add spot" />
              </MiniButton>
            </PanelActions>
            <PanelActions style={{ marginTop: '.4rem' }}>
              <MiniButton type="button" disabled={timed || position?.first}
                title={timed ? t('programAdmin.entry.timedOrder', 'Sorted by its start time: change the time with Edit to move it.')
                  : position?.first ? t('programAdmin.entry.upDisabled', 'This is the first entry of its group.') : undefined}
                onClick={() => actions.moveUp?.(entry)}>
                <ArrowUp size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.up" defaultMessage="Up" />
              </MiniButton>
              <MiniButton type="button" disabled={timed || position?.last}
                title={timed ? t('programAdmin.entry.timedOrder', 'Sorted by its start time: change the time with Edit to move it.')
                  : position?.last ? t('programAdmin.entry.downDisabled', 'This is the last entry of its group.') : undefined}
                onClick={() => actions.moveDown?.(entry)}>
                <ArrowDown size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.down" defaultMessage="Down" />
              </MiniButton>
              <MiniButton type="button" onClick={() => actions.moveTo?.(entry)}>
                <MoveRight size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.moveTo" defaultMessage="Move to…" />
              </MiniButton>
              <MiniButton type="button" $danger disabled={!canRemove}
                title={canRemove ? undefined : t('programAdmin.entry.removeBlocked', 'A spot in this entry or below it is confirmed.')}
                onClick={() => actions.removeEntry?.(entry)}>
                <Trash2 size={13} aria-hidden="true" /><FormattedMessage id="programAdmin.entry.remove" defaultMessage="Remove" />
              </MiniButton>
            </PanelActions>
            {timed && (
              <PanelHint>
                <FormattedMessage id="programAdmin.entry.timedHint"
                  defaultMessage="Entries with a start time are sorted by it. To move this one earlier or later, change its time with Edit; Move to… changes where it belongs (another session, day or room)." />
              </PanelHint>
            )}
            {!canRemove && (
              <PanelHint>
                <FormattedMessage id="programAdmin.entry.removeBlockedBy"
                  defaultMessage="{name} is confirmed on “{entry}”. Withdraw them there first - a removal never takes a confirmed person off the program."
                  values={{ name: confirmed?.assignment.personName, entry: nodeTitle(confirmed?.entry || entry, language) }} />
              </PanelHint>
            )}
            {canRemove && invited.length > 0 && (
              <PanelHint>
                <FormattedMessage id="programAdmin.entry.removeInvited"
                  defaultMessage="Removing it cancels {count, plural, one {# open invitation} other {# open invitations}} below it - the dialog offers one cancellation email per person."
                  values={{ count: invited.length }} />
              </PanelHint>
            )}
          </>
        )}
      </PanelSection>

      {/* The talk materials hang off the entry, not off a spot, so they close the panel; a removed
          entry keeps them too (the panel above shows what it forbids, this one what happened). */}
      <SlidesPanel eventId={program.event.eventId} entryId={entry.id} onChanged={onChanged} />
    </Panel>
  );
};

const SidePanel = ({ program, language, selection, people, entryPosition, spotActions, entryActions, onOpenPerson, onOpenSpot, onChanged, onClose }) => {
  if (!selection) return null;
  if (selection.kind === 'person') {
    // The person's spots at this event, read from the program, each opening its spot panel.
    return (
      <PersonPanel key={selection.id} personId={selection.id} spots={spotsOfPerson(program, selection.id)} language={language}
        onOpenSpot={onOpenSpot} onChanged={onChanged} onClose={onClose} />
    );
  }
  if (selection.kind === 'spot') {
    return (
      <SpotPanel program={program} language={language} spot={selection.assignment} entry={selection.entry}
        people={people} actions={spotActions || {}} onOpenPerson={onOpenPerson} onClose={onClose} />
    );
  }
  return (
    <EntryPanel program={program} language={language} entry={selection.entry} position={entryPosition}
      actions={entryActions || {}} onChanged={onChanged} onClose={onClose} />
  );
};

export default SidePanel;
